/**
 * Состав ленты для расширения: что сейчас наверху и сколько на это надо.
 *
 * Панель расширения умеет читать остатки на счетах, но не знает, чего эти
 * остатки должны хватать. Лента знает суммы ног, но про счета человека не знает
 * ничего — и не должна: остатки живут в браузере рядом с человеком и на сервер
 * не уезжают. Список составов — единственное, чем эти два знания соединяются.
 *
 * Едет только необходимое: адрес площадки, сумма ноги и обещанная прибыль. Ни
 * имён, ни кэфов, ни идентификаторов человека — расширению для ответа «вот эту
 * вилку вам нечем закрыть» больше ничего не нужно.
 */

import type { ForkNeed } from '../money/short';

export const ROSTER = 'forq:roster';

export interface Roster {
  forks: ForkNeed[];
  at: number;
  /**
   * Лучшее окно суток по карте часов человека, МСК (`next/shift.ts`). По нему
   * расширение решает, когда греть вкладки контор. `null` — карта не собралась
   * или оказалась ровной; тогда работает запасное окно, вшитое в расширение.
   */
  window: { from: number; to: number } | null;
}

/** Сколько строк отдаём: панель читают глазами, а не листают. */
export const TOP = 12;

function hostOf(url: string | null): string | null {
  if (url === null || url === '') return null;
  try {
    return new URL(url).host;
  } catch {
    return null;
  }
}

/**
 * Собрать состав из строк ленты.
 *
 * Нога без адреса пропускается: остаток читается по хосту, а без него счёт с
 * ногой не свяжешь. Строка, у которой не осталось ни одной ноги с адресом, в
 * состав не идёт вовсе — про неё панель честно ничего не скажет.
 */
export function rosterOf(
  rows: readonly {
    id: string;
    profit: number;
    legs: readonly { source: string; url: string | null; need: number; cur: string }[];
  }[],
  window: { from: number; to: number } | null = null,
): Roster {
  const forks: ForkNeed[] = [];
  for (const row of rows.slice(0, TOP)) {
    const legs = [];
    for (const leg of row.legs) {
      const host = hostOf(leg.url);
      if (host === null) continue;
      const one: ForkNeed['legs'][number] = { host, venue: leg.source, need: leg.need, cur: leg.cur };
      if (leg.url) one.url = leg.url;
      legs.push(one);
    }
    if (legs.length > 0) forks.push({ id: row.id, profit: row.profit, legs });
  }
  return { forks, at: Date.now(), window };
}

/**
 * Разобрать то, что пришло в окно. `null` — это не наш состав.
 *
 * Проверяется каждое поле по той же причине, что и в обещании: сообщение в окне
 * может положить кто угодно, а на этих числах человек решает, куда донести
 * деньги.
 */
export function parseRoster(data: unknown): Roster | null {
  if (typeof data !== 'object' || data === null) return null;
  const body = data as Record<string, unknown>;
  if (body.channel !== ROSTER) return null;
  const roster = body.roster;
  if (typeof roster !== 'object' || roster === null) return null;
  const one = roster as Record<string, unknown>;
  if (!Array.isArray(one.forks)) return null;
  const forks: ForkNeed[] = [];
  for (const raw of one.forks) {
    if (typeof raw !== 'object' || raw === null) continue;
    const fork = raw as Record<string, unknown>;
    if (typeof fork.id !== 'string' || typeof fork.profit !== 'number') continue;
    if (!Array.isArray(fork.legs)) continue;
    const legs = [];
    for (const rawLeg of fork.legs) {
      if (typeof rawLeg !== 'object' || rawLeg === null) continue;
      const leg = rawLeg as Record<string, unknown>;
      if (typeof leg.host !== 'string' || leg.host === '') continue;
      if (typeof leg.need !== 'number' || !Number.isFinite(leg.need)) continue;
      legs.push({
        host: leg.host,
        venue: typeof leg.venue === 'string' ? leg.venue : leg.host,
        need: leg.need,
        cur: typeof leg.cur === 'string' ? leg.cur : '₽',
        ...(typeof leg.url === 'string' && leg.url !== '' ? { url: leg.url } : {}),
      });
    }
    if (legs.length > 0) forks.push({ id: fork.id, profit: fork.profit, legs });
  }
  const at = typeof one.at === 'number' && Number.isFinite(one.at) ? one.at : 0;
  return { forks, at, window: hoursOf(one.window) };
}

/** Окно суток из сообщения. Мусор и часы вне суток — это `null`, а не догадка. */
function hoursOf(raw: unknown): { from: number; to: number } | null {
  if (typeof raw !== 'object' || raw === null) return null;
  const window = raw as Record<string, unknown>;
  const from = window.from;
  const to = window.to;
  if (typeof from !== 'number' || typeof to !== 'number') return null;
  if (!Number.isInteger(from) || !Number.isInteger(to)) return null;
  if (from < 0 || from > 23 || to < 0 || to > 23) return null;
  return { from, to };
}

export function rosterEnvelope(roster: Roster): { channel: string; roster: Roster } {
  return { channel: ROSTER, roster };
}
